import { TRANSACTION_TYPES } from "../constants"
import { ChainId } from "../constants/pools/types/ChainId"
import { notifyCustomError, notifyHandler } from "../utils/notifyHandler"
import { useCallback, useEffect, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useLockedStakeContract } from "./useContract"

import { AppState } from "../state"
import { BigNumber } from "@ethersproject/bignumber"
import { Zero } from "@ethersproject/constants"
import checkAndApproveTokenForTrade from "../utils/checkAndApproveTokenForTrade"
import { updateLastTransactionTimes } from "../state/application"
import { useActiveWeb3React } from "."
import { LpTokenUnguarded } from "../../types/ethers-contracts"

export function useLockedRewardsHelpers(
  rewardPids: Record<ChainId, number | null> | null,
  lpTokenContract: LpTokenUnguarded | null
): {
  isPoolIncentivized: boolean;
  approveAndStake: (amount: BigNumber) => Promise<void>;
  lpAmountStakedHandleError: boolean;
  setLpAmountStakedHandleError: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  setAmountStakedHandle: (value: (((prevState: string) => string) | string)) => void;
  amountStaked: BigNumber;
  amountStakedLocked: BigNumber;
  amountStakedUnlocked: BigNumber;
  unstake: () => Promise<void>;
  amountStakedHandle: string;
  rewardAmount: BigNumber } {
  const { account, chainId } = useActiveWeb3React()
  const dispatch = useDispatch()
  const infiniteApproval = useSelector((state: AppState) => state.user.infiniteApproval)
  // const rewardsContract = useMiniChefContract()
  const lockedStakerContract = useLockedStakeContract()
  const poolPid = chainId && rewardPids ? rewardPids[chainId] : null
  const [amountStaked, setAmountStaked] = useState(Zero)
  const [amountStakedLocked, setAmountStakedLocked] = useState(Zero)
  const [amountStakedUnlocked, setAmountStakedUnlocked] = useState(Zero)
  const [rewardAmount, setRewardAmount] = useState(Zero)
  const [amountStakedHandle, setAmountStakedHandle] = useState('0')
  const [lpAmountStakedHandleError, setLpAmountStakedHandleError] = useState(false)
  const lastTransactionTimes = useSelector(
    (state: AppState) => state.application.lastTransactionTimes,
    shallowEqual
  )
  const lastStakeOrClaim =
    lastTransactionTimes[TRANSACTION_TYPES.STAKE_OR_CLAIM]

  const approveAndStake = useCallback(
    async (amount: BigNumber) => {
      if (!lpTokenContract || !lockedStakerContract || !account || poolPid === null)
        return
      try {
        await checkAndApproveTokenForTrade(
          lpTokenContract,
          lockedStakerContract.address,
          account,
          amount,
          infiniteApproval,
          BigNumber.from(1),
        )
        const txn = await lockedStakerContract.stake(amount)
        notifyHandler(txn?.hash, "deposit")
        await txn.wait()
        dispatch(
          updateLastTransactionTimes({
            [TRANSACTION_TYPES.STAKE_OR_CLAIM]: Date.now(),
          }),
        )
      } catch (e) {
        console.error(e)
        notifyCustomError({ ...(e as Error), message: "Unable to Stake" })
      }
    },
    [
      lpTokenContract,
      lockedStakerContract,
      account,
      infiniteApproval,
      poolPid,
      dispatch,
    ],
  )

  const unstake = useCallback(
    async () => {
      if (!lpTokenContract || !lockedStakerContract || !account || poolPid === null)
        return
      try {
        const txn = await lockedStakerContract.exit()
        notifyHandler(txn?.hash, "withdraw")
        await txn.wait()
        dispatch(
          updateLastTransactionTimes({
            [TRANSACTION_TYPES.STAKE_OR_CLAIM]: Date.now(),
          }),
        )
      } catch (e) {
        console.error(e)
        notifyCustomError({ ...(e as Error), message: "Unable to Unstake" })
      }
    },
    [lpTokenContract, lockedStakerContract, account, poolPid, dispatch],
  )

  useEffect(() => {
    async function fetchAmount() {
      if (!lockedStakerContract || !account || poolPid === null) {
        setAmountStaked(Zero)
        setAmountStakedLocked(Zero)
        setAmountStakedUnlocked(Zero)
        return
      }
      const lockedBalances = await lockedStakerContract
        .lockedBalances(account)
        .catch(console.error)
      setAmountStaked(lockedBalances ? lockedBalances.total : Zero)
      setAmountStakedLocked(lockedBalances ? lockedBalances.locked : Zero)
      setAmountStakedUnlocked(lockedBalances ? lockedBalances.unlockable : Zero)

      // reset to default input-value
      setAmountStakedHandle("0")

      // auto-setting of MAX value
      // setAmountStakedHandle(userInfo ? new BigNumberJs(userInfo.amount.toString()).dividedBy(new BigNumberJs(10 ** 18).toString()).toString() : '0')
    }
    void fetchAmount()
  }, [account, poolPid, lockedStakerContract, lastStakeOrClaim])

  useEffect(() => {
    async function fetchRewardAmount() {
      if (!lockedStakerContract || !account || poolPid === null) return
      const userInfo = await lockedStakerContract
        .claimableRewards(account)
        .catch(console.error)
      setRewardAmount(userInfo ? userInfo[0].amount : Zero)
    }
    void fetchRewardAmount()
  }, [account, poolPid, lockedStakerContract, lastStakeOrClaim])

  return {
    approveAndStake,
    unstake,
    amountStaked,
    amountStakedLocked,
    amountStakedUnlocked,
    amountStakedHandle,
    setAmountStakedHandle,
    lpAmountStakedHandleError,
    setLpAmountStakedHandleError,
    isPoolIncentivized: poolPid !== null,
    rewardAmount
  }
}
